// Typed translator over a nested catalogue. Keys are dotted paths ("auth.otp.title"); placeholder
// names come from the strings themselves ("{email}"), so a missing or misspelled parameter is a type
// error. Plural entries are `{ one, other }` objects selected with Intl.PluralRules and `count`.

export type PluralEntry = { readonly one: string; readonly other: string };
export type Entry = string | PluralEntry;
export interface Catalogue {
  readonly [key: string]: Entry | Catalogue;
}

/** Every leaf key of a catalogue as a dotted path. */
export type KeyOf<T, P extends string = ""> = {
  [K in keyof T & string]: T[K] extends Entry ? `${P}${K}` : KeyOf<T[K], `${P}${K}.`>;
}[keyof T & string];

/** The entry at a dotted path. */
export type EntryAt<T, K extends string> = K extends `${infer H}.${infer R}`
  ? H extends keyof T
    ? EntryAt<T[H], R>
    : never
  : K extends keyof T
    ? T[K]
    : never;

type Placeholders<S> = S extends `${string}{${infer P}}${infer Rest}`
  ? P | Placeholders<Rest>
  : never;
export type ParamsOf<E> = E extends string
  ? Placeholders<E>
  : E extends PluralEntry
    ? Placeholders<E["one"]> | Placeholders<E["other"]> | "count"
    : never;

export type ParamValue = string | number;
export type Params<N extends string> = { [P in N]: ParamValue };

/** Rest-argument tuple: no params argument when the string has no placeholders. */
export type ArgsFor<T, K extends string> = [ParamsOf<EntryAt<T, K>>] extends [never]
  ? []
  : [params: Params<ParamsOf<EntryAt<T, K>>>];

export interface Translator<T extends Catalogue> {
  readonly locale: string;
  t<K extends KeyOf<T>>(key: K, ...args: ArgsFor<T, K>): string;
  /** True when `key` exists (for dynamic keys such as error codes). */
  has(key: string): key is KeyOf<T>;
  /** Looks up a runtime key and falls back to `fallback` when it does not exist. */
  tDynamic(key: string, fallback: KeyOf<T>, params?: Record<string, ParamValue>): string;
}

function lookup(catalogue: Catalogue, key: string): Entry | undefined {
  let node: Entry | Catalogue | undefined = catalogue;
  for (const part of key.split(".")) {
    if (node === undefined || typeof node === "string" || isPlural(node)) return undefined;
    node = (node as Catalogue)[part];
  }
  if (node === undefined) return undefined;
  if (typeof node === "string" || isPlural(node)) return node;
  return undefined;
}

function isPlural(node: unknown): node is PluralEntry {
  return (
    typeof node === "object" &&
    node !== null &&
    typeof (node as PluralEntry).one === "string" &&
    typeof (node as PluralEntry).other === "string"
  );
}

function interpolate(template: string, params: Record<string, ParamValue> | undefined): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : whole,
  );
}

export function createTranslator<T extends Catalogue>(catalogue: T, locale = "en"): Translator<T> {
  const plural = new Intl.PluralRules(locale);
  const numberFormat = new Intl.NumberFormat("en-IN");
  const render = (entry: Entry, params?: Record<string, ParamValue>): string => {
    if (typeof entry === "string") return interpolate(entry, params);
    const count = Number(params?.count ?? 0);
    const form = plural.select(count) === "one" ? entry.one : entry.other;
    // Counts are shown with en-IN grouping ("1,200 orders").
    return interpolate(form, { ...params, count: numberFormat.format(count) });
  };
  return {
    locale,
    t(key, ...args) {
      const entry = lookup(catalogue, key);
      if (entry === undefined) throw new Error(`Missing string: ${key}`);
      return render(entry, args[0] as Record<string, ParamValue> | undefined);
    },
    has(key): key is KeyOf<T> {
      return lookup(catalogue, key) !== undefined;
    },
    tDynamic(key, fallback, params) {
      const entry = lookup(catalogue, key) ?? lookup(catalogue, fallback);
      if (entry === undefined) throw new Error(`Missing string: ${fallback}`);
      return render(entry, params);
    },
  };
}
